import {
  Box,
  CssBaseline,
  Divider,
  ThemeProvider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Header from '../Header/Header';
import FooterArea from '../Footer/FooterArea';
import { legalInsightsTheme } from '../../LegalInsightsTheme';
import { Outlet } from 'react-router';
import AlmaHeader from '../Header/AlmaHeader';
import { useAppDispatch } from '../../store/hooks';
import { useCallback, useContext, useEffect } from 'react';
import { initAuthorsAsync } from '../../store/authors';
import { initCategoriesAsync } from '../../store/categories';
import { addEventToGTMDataLayer } from '../../utilities/gtm';
import { AuthContext } from '../Auth/AuthProvider';
import { useUserAnalytics } from '../../features/Analytics/hooks/useUserAnalytics';
import { hasSubscriptionAccess } from '../../features/Authentication/utils/AuthUtilities';
import { IIdentityContext } from '@almamedia-legal-insights/types';
import MigrationBanner from '../Auth/MigrationBanner';

export default function SiteContainer() {
  const theme = useTheme();
  const showAlmaHeader = useMediaQuery(theme.breakpoints.up('sm'));

  const dispatch = useAppDispatch();
  const auth = useContext(AuthContext);
  const isIpAuthenticated = auth.authInfo?.ipAuth;
  const hasIpAccess = auth.authInfo?.ipRights;
  const isAlmaAccountAuthenticated = auth.authInfo?.almaIdAuth;
  const hasAlmaAccountAccess = auth.authInfo?.almaIdRights;

  // Setup user analytics
  const { authInfo, actions }: IIdentityContext = useContext(AuthContext);
  const getTokenCallback = useCallback(() => {
    return actions?.getAccessToken?.() ?? Promise.resolve('');
  }, [actions]);
  useUserAnalytics({
    enabled: hasSubscriptionAccess(authInfo),
    getToken: getTokenCallback,
  });

  // Get authors and categories on page load
  useEffect(() => {
    dispatch(initAuthorsAsync());
    dispatch(initCategoriesAsync());
  }, [dispatch]);

  // Update access info to GTM when it changes
  useEffect(() => {
    addEventToGTMDataLayer({
      ip_authenticated: isIpAuthenticated,
      ip_access: hasIpAccess,
      alma_account_authenticated: isAlmaAccountAuthenticated,
      alma_account_access: hasAlmaAccountAccess,
    });
  }, [
    isIpAuthenticated,
    hasIpAccess,
    isAlmaAccountAuthenticated,
    hasAlmaAccountAccess,
  ]);

  return (
    <ThemeProvider theme={legalInsightsTheme}>
      <CssBaseline>
        {showAlmaHeader && (
          <>
            <AlmaHeader />
            <Divider />
          </>
        )}
        <MigrationBanner />
        <Header />
        <Box
          id='parent-container'
          sx={{
            flex: 1,
          }}
        >
          <Outlet />
        </Box>
        <FooterArea />
      </CssBaseline>
    </ThemeProvider>
  );
}
